// frontend/src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { login, getMe } from "../services/authService";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Alert,
  Tabs,
  Tab,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  InputAdornment,
} from "@mui/material";
import {
  School,
  SupervisorAccount,
  AdminPanelSettings,
} from "@mui/icons-material";

// ――― 学生用ドメイン定義 ――― //
const STUDENT_DOMAINS = [
  { key: "musashino", label: "武蔵野", domain: "@stu.musashino-u.ac.jp" },
  { key: "otsuma",    label: "大妻",   domain: "@cst.otsuma.ac.jp" },
];

const Login = () => {
  const [email, setEmail]                           = useState("");
  const [password, setPassword]                     = useState("");
  const [error, setError]                           = useState("");
  const [loading, setLoading]                       = useState(true);
  const [activeTab, setActiveTab]                   = useState(0);      // 0: student 1: teacher 2: admin
  const [studentDomainKey, setStudentDomainKey]     = useState("musashino");
  const navigate                                    = useNavigate();

  // ユーザータイプ定義
  const userTypes = [
    {
      label: "学生",
      icon: <School />,
      placeholder: "学籍番号7桁",
    },
    {
      label: "教員",
      icon: <SupervisorAccount />,
      placeholder: "メールアドレス",
      domain: "@ds.musashino-u.ac.jp",
    },
    {
      label: "管理者",
      icon: <AdminPanelSettings />,
      placeholder: "管理者メールアドレス",
      domain: "@ds.musashino-u.ac.jp",
    },
  ];

  // ――― 既ログインチェック ――― //
  useEffect(() => {
    const checkLoginStatus = async () => {
      const token = localStorage.getItem("token");
      if (!token) return setLoading(false);

      try {
        const userInfo = await getMe();
        if (userInfo.role === "admin")   navigate("/admin/users");
        else if (userInfo.role === "teacher") navigate("/teacher/home");
        else navigate("/student/home");
      } catch {
        localStorage.removeItem("token");
        setLoading(false);
      }
    };
    checkLoginStatus();
  }, [navigate]);

  // ――― 入力ハンドラ ――― //
  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setEmail("");
    setError("");
  };

  const handleEmailChange = (e) => {
    let value = e.target.value;
    if (activeTab === 0) {
      value = value.replace(/[^0-9]/g, "").slice(0, 7); // 学籍番号は数字7桁のみ
    }
    setEmail(value);
  };

  // ――― メールアドレスの合成 ――― //
  const getFullEmail = () => {
    if (activeTab === 0) {
      const dom = STUDENT_DOMAINS.find((d) => d.key === studentDomainKey).domain;
      return `s${email}${dom}`;
    }
    const dom = userTypes[activeTab].domain;
    return email.includes("@") || !dom ? email : `${email}${dom}`;
  };

  // ――― ログイン処理 ――― //
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const fullEmail = getFullEmail();
      const data      = await login(fullEmail, password); // ①認証
      localStorage.setItem("token", data.access_token);
      const userInfo  = await getMe();                    // ②ロール取得

      if (userInfo.role === "admin")       navigate("/admin/users");
      else if (userInfo.role === "teacher") navigate("/teacher/home");
      else                                  navigate("/student/home");
    } catch (err) {
      setError(err.response?.data?.detail || "ログインに失敗しました");
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <CircularProgress />
      </Container>
    );
  }

  // ――― JSX ――― //
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Paper elevation={3} sx={{ p: 4, width: "100%", bgcolor: "#000" }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Co-Edu ログイン
          </Typography>

          {/* タブ：学生 / 教員 / 管理者 */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            aria-label="ユーザータイプ選択"
            sx={{ mb: 4 }}
          >
            {userTypes.map((type, i) => (
              <Tab
                key={type.label}
                icon={type.icon}
                label={type.label}
                sx={{
                  fontWeight: activeTab === i ? "bold" : "normal",
                  color: activeTab === i ? "primary.main" : "text.primary",
                }}
              />
            ))}
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* ――― フォーム本体 ――― */}
          <form onSubmit={handleLogin} autoComplete="off" noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {userTypes[activeTab].label}としてログイン
              </Typography>

              {/* 学生のみ：ドメイン切替トグル */}
              {activeTab === 0 && (
                <ToggleButtonGroup
                  value={studentDomainKey}
                  exclusive
                  onChange={(_, v) => v && setStudentDomainKey(v)}
                  fullWidth
                  sx={{ mb: 2 }}
                  color="primary"
                >
                  {STUDENT_DOMAINS.map((d) => (
                    <ToggleButton key={d.key} value={d.key}>
                      {d.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}

              {/* メール／学籍番号入力 */}
              <TextField
                label={userTypes[activeTab].placeholder}
                fullWidth
                margin="normal"
                value={email}
                onChange={handleEmailChange}
                required
                type={activeTab === 0 ? "text" : "email"}
                autoComplete={activeTab === 0 ? "one-time-code" : "username"}
                inputMode={activeTab === 0 ? "numeric" : "email"}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                name={activeTab === 0 ? "coedu-studentid" : "coedu-username"}
                inputProps={{
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                  inputMode: activeTab === 0 ? "numeric" : "email",
                }}
                InputProps={{
                  inputMode: activeTab === 0 ? "numeric" : "text",
                  startAdornment:
                    activeTab === 0 ? (
                      <InputAdornment position="start">s</InputAdornment>
                    ) : null,
                  endAdornment:
                    activeTab === 0 ? (
                      <InputAdornment position="end">
                        {
                          STUDENT_DOMAINS.find(
                            (d) => d.key === studentDomainKey
                          ).domain
                        }
                      </InputAdornment>
                    ) : activeTab !== 2 ? (
                      <InputAdornment position="end">
                        {userTypes[activeTab].domain}
                      </InputAdornment>
                    ) : null,
                }}
              />

              {/* パスワード */}
              <TextField
                label="パスワード"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                name="coedu-password"
                inputProps={{
                  "data-lpignore": "true",
                  "data-1p-ignore": "true",
                  "data-bwignore": "true",
                }}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2, mb: 3, py: 1.5 }}
              startIcon={userTypes[activeTab].icon}
              disabled={
                !password ||
                (activeTab === 0 ? email.length !== 7 : !email.trim())
              }
            >
              ログイン
            </Button>

            <Divider sx={{ my: 2 }} />

            {/* 登録は未実装のため案内を非表示 */}
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;